import { Injectable, signal, computed } from '@angular/core';

/**
 * A resolved impersonation target. The admin is always the real identity; this
 * only describes whose *view* is being reproduced (read-only).
 */
export type ImpersonationTarget =
  | { kind: 'officer'; empId: string; label: string }
  | { kind: 'scope'; scopeType: 'reporting_unit' | 'department'; id: string; label: string }
  | { kind: 'group'; group: string; label: string };

/**
 * Holds the current impersonation target.
 *
 * Deliberately tiny and dependency-free so it can be shared by both
 * `JsonaService` (reads it to attach the X-Impersonate-* headers) and
 * `PermissionService` (writes it) without a circular DI dependency.
 */
@Injectable({ providedIn: 'root' })
export class ImpersonationContextService {
  readonly target = signal<ImpersonationTarget | null>(null);

  readonly isImpersonating = computed(() => this.target() !== null);
  readonly label = computed(() => this.target()?.label ?? '');

  /** Officer emp_id when impersonating an officer, else null (back-compat). */
  readonly impersonatedEmpId = computed(() => {
    const t = this.target();
    return t?.kind === 'officer' ? t.empId : null;
  });

  set(target: ImpersonationTarget | null): void {
    this.target.set(target);
  }

  clear(): void {
    this.target.set(null);
  }

  /**
   * Build the X-Impersonate-* request headers for a target (or the current one).
   * Returned as a plain map so callers can merge into HttpHeaders.
   */
  headersFor(target: ImpersonationTarget | null = this.target()): Record<string, string> {
    if (!target) return {};
    switch (target.kind) {
      case 'officer':
        return { 'X-Impersonate-Officer': target.empId };
      case 'scope':
        return { 'X-Impersonate-Scope-Type': target.scopeType, 'X-Impersonate-Scope-Id': target.id };
      case 'group':
        return { 'X-Impersonate-Group': target.group };
    }
  }
}
